import { cx } from '@orn/shared-web/utils/cx';

import { FinancialAccountSubtype } from '../../__generated__/globalTypes';
import { type FinancialAccount } from '../../hooks/queries/useFinancialAccounts';
import { useDeviceBreakpoints } from '../../hooks/useDeviceBreakpoints';
import { subtypeToDisplayName } from '../../utils/accounts';
import { toCurrency } from '../../utils/formatting';
import AccountRow from '../AccountRow';
import BalanceChangeIndicator from '../changeIndicators/BalanceChangeIndicator';
import { getPresetDateRange } from '../DateRangePicker';
import Widget from '../Widget';

import type { ColumnTable } from 'arquero';

interface IAccountGroupProps {
  subtype: FinancialAccountSubtype;
  accounts: FinancialAccount[];
  transactions?: ColumnTable | null;
  onAccountClick?: (accountId: string) => void;
  /** Force compact layout for narrow containers like sidebars */
  compact?: boolean;
}

const AccountGroup = ({ subtype, accounts, transactions, onAccountClick, compact = false }: IAccountGroupProps) => {
  const { isDesktop } = useDeviceBreakpoints();
  const useCompactLayout = compact || !isDesktop;

  if (accounts.length === 0) {
    return null;
  }

  // Check if all accounts in this section are syncing
  const allAccountsSyncing = accounts.every((account) => !account.bankConnection?.lastSyncedAt);

  // Calculate sum of only synced account balances
  const balanceSum = accounts.reduce((acc, account) => {
    if (account.bankConnection?.lastSyncedAt && account.currentBalance?.balance) {
      return acc + parseFloat(account.currentBalance.balance);
    }
    return acc;
  }, 0);

  const totalBalance = allAccountsSyncing ? null : (
    <span
      className={cx(
        'text-lg font-semibold',
        subtype === FinancialAccountSubtype.CreditCard ? 'text-error-primary' : 'text-success-primary'
      )}
    >
      {toCurrency(balanceSum)}
    </span>
  );

  // Hide change indicator on mobile/compact layouts to match mockup
  const changeIndicator =
    !useCompactLayout &&
    !allAccountsSyncing &&
    (subtype === FinancialAccountSubtype.Checking || subtype === FinancialAccountSubtype.Savings) ? (
      <BalanceChangeIndicator
        transactions={transactions ?? null}
        currentBalance={balanceSum}
        dateRange={getPresetDateRange('thisYear')}
      />
    ) : null;

  const title = (
    <div className="flex items-center gap-2">
      <span>{subtypeToDisplayName(subtype)}</span>
      {changeIndicator}
    </div>
  );

  return (
    <Widget collapsible>
      <Widget.Header title={title} trailing={totalBalance} />
      <Widget.Body>
        {accounts.map((financialAccount) => (
          <AccountRow
            key={financialAccount.id}
            financialAccount={financialAccount}
            onClick={onAccountClick}
            compact={compact}
          />
        ))}
      </Widget.Body>
    </Widget>
  );
};

export default AccountGroup;
